/**
 * Desktop OAuth (Tauri / native) wire helpers.
 *
 * BE source: ``ando-agent/src/api/v1/endpoints/auth_desktop.py``
 * — RFC 8252 native-app flow. The Tauri shell opens the SYSTEM browser
 * for the IdP handshake, intercepts the ``ando://callback`` deep-link,
 * then POSTs the authorization code here in exchange for OIDC tokens.
 *
 * Tokens are persisted client-side in the OS keychain (Rust ``keyring``:
 * macOS Keychain / Windows Credential Manager / Secret Service — the OS
 * owns encryption-at-rest, no vault file). Every subsequent API call rides on the
 * ``Authorization: Bearer <access_token>`` header that
 * ``api/dependencies.py::_resolve_token`` already accepts as a fallback
 * to the cookie — dual-mode middleware, no duplicated introspection.
 */

import { apiClient } from "../ApiClient.js";

export interface DesktopUserInfo {
  readonly user_id: string;
  readonly zitadel_user_id: string;
  readonly email?: string | null;
  readonly name?: string | null;
  readonly given_name?: string | null;
  readonly family_name?: string | null;
  readonly preferred_username?: string | null;
  readonly locale?: string | null;
  readonly picture?: string | null;
  /** ANDO ``users.role`` (resolved server-side from the DB, not an OIDC
   *  claim). Drives the desktop platform-admin gate — see
   *  ``readDesktopSession`` in ``auth/session.ts``. */
  readonly role?: string | null;
  readonly product_access?: { readonly workspace: boolean; readonly engine: boolean };
}

export interface DesktopTokensResponse {
  readonly access_token: string;
  readonly refresh_token: string | null;
  readonly id_token: string | null;
  readonly token_type: string;
  readonly expires_in: number;
  readonly user_info: DesktopUserInfo;
}

export interface DesktopExchangeBody {
  readonly code: string;
  readonly code_verifier: string;
  readonly redirect_uri: string;
}

export interface DesktopRefreshBody {
  readonly refresh_token: string;
}

/**
 * ``POST /api/v1/auth/desktop/exchange`` — swap an authorization code
 * for an OIDC token bundle. PUBLIC endpoint (the caller is not yet
 * authenticated) — bypasses the Bearer interceptor.
 */
export async function exchangeDesktopCode(
  body: DesktopExchangeBody,
): Promise<DesktopTokensResponse> {
  // Bypass the auth token provider for this call — the user isn't
  // logged in yet. We do this by passing an empty Authorization
  // header, which the interceptor in ``request()`` honours (caller-set
  // ``Authorization`` always wins).
  return apiClient.post<DesktopTokensResponse>("/auth/desktop/exchange", body, {
    headers: { Authorization: "" },
  });
}

/**
 * ``POST /api/v1/auth/desktop/refresh`` — rotate the access token.
 *
 * Zitadel's default policy rotates the refresh token on every call —
 * the caller MUST overwrite the previous secret in the OS keychain with
 * the new ``refresh_token`` returned here.
 */
export async function refreshDesktopTokens(
  body: DesktopRefreshBody,
): Promise<DesktopTokensResponse> {
  return apiClient.post<DesktopTokensResponse>("/auth/desktop/refresh", body, {
    headers: { Authorization: "" },
  });
}

export interface DesktopLogoutBody {
  readonly refresh_token: string | null;
}

/**
 * ``POST /api/v1/auth/desktop/logout`` — server-side half of desktop
 * sign-out: revokes the OIDC refresh token at Zitadel (RFC 7009) so the
 * bundle wiped from the keychain can never be replayed. The web
 * ``/auth/logout`` only knows the BFF cookie session, which a Bearer-only
 * desktop never has. Best-effort on the server (revocation is
 * non-fatal); the caller still clears the local vault regardless.
 */
export async function revokeDesktopSession(body: DesktopLogoutBody): Promise<void> {
  await apiClient.post<void>("/auth/desktop/logout", body, {
    headers: { Authorization: "" },
  });
}
